import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Skeleton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SendIcon from '@mui/icons-material/Send';
import MetricCard from '../common/MetricCard';
import DataSourceAttribution from '../common/DataSourceAttribution';
import apiService from '../../services/api';
import { usePermissionAccess } from '../auth/RoleGuard';
import FunnelChart from './FunnelChart';
import FollowUpSettings from './FollowUpSettings';
import SurveyEmailsCard from './SurveyEmailsCard';
import CampaignSwitcher from './CampaignSwitcher';
import SurveyAgeReasonChart from './SurveyAgeReasonChart';
import SendInvitesDialog from './SendInvitesDialog';
import RecipientsTab from './tabs/RecipientsTab';
import ResponsesTab from './tabs/ResponsesTab';
import QuestionBreakdownTab from './tabs/QuestionBreakdownTab';
import { FunnelResponse, SurveyCampaign, SyncResult } from './types';

/**
 * Survey Tracking — the whole funnel for the customer discovery survey.
 *
 * Data comes from /api/admin/survey/*, which proxies the standalone survey Lambda
 * (DynamoDB + a snapshot of Brevo's per-recipient email events). Nothing here
 * touches Aurora.
 *
 * The KPI row sits ABOVE the tabs so the headline numbers stay visible whichever
 * tab is open — the same choice ProductAnalyticsPage makes.
 */

const TEAL = '#1A5F7A';
const TAB_COLORS = ['#1A5F7A', '#D97706', '#146152'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`survey-tracking-tabpanel-${index}`}
      aria-labelledby={`survey-tracking-tab-${index}`}
    >
      {/* Mounted only when active, so a hidden tab does not fetch. */}
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const relativeTime = (iso: string | null) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} d ago`;
};

export const SurveyTrackingPage: React.FC = () => {
  const { hasFullAccess } = usePermissionAccess();
  // A view_only admin can open this page, so without this gate they would press
  // Refresh or Send and eat a 403 from the API. Same precedent as FollowUpSettings.
  const canEdit = hasFullAccess('audience_manager');

  const [funnel, setFunnel] = useState<FunnelResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [refreshingRoster, setRefreshingRoster] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  // Which survey the whole page is about. Null until the switcher has loaded the
  // list and picked the live one, which is why loadFunnel waits for it — firing a
  // request with no campaign would show the default campaign's numbers for a beat
  // and then replace them, which reads as a glitch.
  const [campaign, setCampaign] = useState<string | null>(null);
  // The selected campaign itself, so the heading names the survey whose numbers are
  // on screen. It was hardcoded, which meant the page confidently said "Customer
  // Discovery Survey" over a different campaign's funnel — the one thing on the page
  // that did not follow the switcher, and the most authoritative-looking.
  const [campaignMeta, setCampaignMeta] = useState<SurveyCampaign | null>(null);

  const selectCampaign = (id: string, meta?: SurveyCampaign) => {
    setCampaign(id);
    setCampaignMeta(meta ?? null);
    // A sync or send note belongs to the campaign it was produced for. Carrying it
    // across a switch reports another survey's result over these numbers.
    setSyncNote(null);
  };

  const loadFunnel = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getSurveyFunnel(campaign || undefined);
      setFunnel(res || null);
    } catch (e: any) {
      // Surfaced, not swallowed. The most likely cause is the admin-backend task
      // definition missing SURVEY_API_URL / SURVEY_INTERNAL_API_KEY, and an empty
      // dashboard would look like "no data" instead of "not configured".
      setError(
        e?.message === 'Unauthorized'
          ? 'Your session expired. Reload the page to sign in again.'
          : e?.message || 'Could not load the survey funnel.'
      );
      setFunnel(null);
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (campaign) loadFunnel();
  }, [loadFunnel, campaign]);

  const sync = async () => {
    setSyncing(true);
    setSyncNote(null);
    try {
      const res: SyncResult = await apiService.syncSurveyTracking();
      const events = Object.values(res?.tags || {}).reduce((n, t: any) => n + (t?.events || 0), 0);
      setSyncNote(
        res?.ok
          ? `Synced ${events} email event${events === 1 ? '' : 's'} across ${res.recipients} recipient${res.recipients === 1 ? '' : 's'}.`
          : `Partly synced — ${res?.errors?.map((e) => `${e.tag}: ${e.error}`).join('; ') || 'some tags failed'}. The figures below may be incomplete.`
      );
      await loadFunnel();
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      setSyncNote(`Sync failed: ${e?.message || 'unknown error'}`);
    }
    setSyncing(false);
  };

  /**
   * Pulls newly eligible members in from Aurora. Never sends anything — adding
   * someone and mailing them are deliberately two separate presses, so a refresh
   * can be run freely to see who is new.
   */
  const refreshRoster = async () => {
    setRefreshingRoster(true);
    setSyncNote(null);
    try {
      const res = await apiService.refreshSurveyRoster(undefined, campaign || undefined);
      const added = Number(res?.added || 0);
      const existing = Number(res?.existing || 0);
      const suppressed = Number(res?.suppressed || 0);

      const parts = [
        `Added ${added} new member${added === 1 ? '' : 's'}`,
        `${existing} already on the list`,
      ];
      if (suppressed > 0) parts.push(`${suppressed} skipped (removed earlier)`);

      setSyncNote(
        added > 0
          ? `Synced ${parts.join(', ')}. Press "Send invitations" to email them.`
          : `Synced ${parts.join(', ')}. Nobody new to add.`
      );
      await loadFunnel();
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      setSyncNote(
        e?.message?.includes('PERMISSION') || e?.message?.includes('403')
          ? 'Sync failed: you have view-only access to this area.'
          : `Sync failed: ${e?.message || 'unknown error'}`
      );
    }
    setRefreshingRoster(false);
  };

  const handleSent = async (s: { sent: number; remaining: number; failed: number }) => {
    const bits = [`Sent ${s.sent} invitation${s.sent === 1 ? '' : 's'}`];
    if (s.failed > 0) bits.push(`${s.failed} failed`);
    if (s.remaining > 0) bits.push(`${s.remaining} still waiting — press Send again`);
    setSyncNote(`${bits.join('. ')}.`);
    await loadFunnel();
    setRefreshKey((k) => k + 1);
  };

  const stages = funnel?.stages || [];
  const completed = stages.find((s) => s.key === 'completed')?.count ?? 0;
  const opened = stages.find((s) => s.key === 'opened')?.count ?? 0;
  const delivered = stages.find((s) => s.key === 'delivered')?.count ?? 0;
  const landed = stages.find((s) => s.key === 'viewed')?.count ?? 0;

  return (
    <Box sx={{ p: 3, bgcolor: '#F9FAFB', minHeight: '100vh' }}>
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '.1em', color: '#9CA3AF', mb: 1 }}>
        ANALYTICS &amp; INSIGHTS → SURVEY TRACKING
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, minWidth: 260 }}>
          {/* A real <h1>: it is the page's title, screen readers should announce it as
              one, and it gives the journey test something unambiguous to assert on —
              the campaign name also appears inside the switcher. */}
          <Typography component="h1" sx={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '30px', fontWeight: 700, color: '#111827' }}>
            {campaignMeta?.title || 'Customer Discovery Survey'}
          </Typography>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#6B7280', mb: 1.5 }}>
            Who was invited, what the email did, and who answered.
          </Typography>
          <CampaignSwitcher value={campaign} onChange={selectCampaign} />
        </Box>
        {/*
          Three distinct actions, ordered by how far each reaches: Sync only reads
          from Brevo, Refresh adds members without mailing them, Send is the one
          that puts email in front of people. The <span> inside each Tooltip is what
          keeps the tooltip working while the button is disabled.
        */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Pull the latest delivery, open and click events from Brevo" arrow>
            <span>
              <Button
                variant="outlined"
                onClick={sync}
                disabled={syncing}
                startIcon={<RefreshIcon sx={{ animation: syncing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />}
                sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 600, textTransform: 'none', borderColor: TEAL, color: TEAL }}
              >
                {syncing ? 'Syncing…' : 'Sync now'}
              </Button>
            </span>
          </Tooltip>

          <Tooltip
            title={
              canEdit
                ? 'Find members who created their first chapter in the last 30 days and add them to the list. Does not send anything.'
                : 'You have view-only access to this area.'
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                onClick={refreshRoster}
                disabled={!canEdit || refreshingRoster}
                startIcon={<GroupAddIcon />}
                sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 600, textTransform: 'none', borderColor: TEAL, color: TEAL }}
              >
                {refreshingRoster ? 'Refreshing…' : 'Refresh list'}
              </Button>
            </span>
          </Tooltip>

          <Tooltip
            title={
              canEdit
                ? 'Email the survey to everyone on the list who has never been sent it'
                : 'You have view-only access to this area.'
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={() => setSendOpen(true)}
                disabled={!canEdit}
                startIcon={<SendIcon />}
                sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 600, textTransform: 'none', bgcolor: TEAL, '&:hover': { bgcolor: '#14506B' } }}
              >
                Send invitations
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <SendInvitesDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onSent={handleSent}
        campaign={campaign}
        campaignTitle={campaignMeta?.title || null}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, fontFamily: "'Montserrat', sans-serif", fontSize: '13px' }}>
          {error}
        </Alert>
      )}
      {syncNote && (
        <Alert
          // Failures are the only thing that should read as a warning. Matching on
          // the failure word rather than the success word, so a new success message
          // does not silently start rendering as a warning.
          severity={/^(Sync failed|Partly synced)|failed:/i.test(syncNote) ? 'warning' : 'success'}
          onClose={() => setSyncNote(null)}
          sx={{ mb: 3, fontFamily: "'Montserrat', sans-serif", fontSize: '13px' }}
        >
          {syncNote}
        </Alert>
      )}

      {/* ── KPI row ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {[
          { title: 'Invited', value: funnel?.invited ?? 0, subtitle: 'members sent a personal link' },
          { title: 'Delivered', value: delivered, subtitle: 'reached the inbox' },
          { title: 'Opened', value: opened, subtitle: 'opened the email' },
          { title: 'Landed on survey', value: landed, subtitle: 'clicked through to the page' },
          { title: 'Completed', value: completed, subtitle: 'finished the survey' },
          {
            title: 'Finish rate',
            value: funnel?.completion_rate_of_viewed ?? 0,
            suffix: '%',
            subtitle: 'of those who reached the page',
          },
        ].map((kpi) => (
          <Box key={kpi.title} sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 150, display: 'flex' }}>
            <MetricCard
              variant="compact"
              title={kpi.title}
              value={kpi.value}
              suffix={(kpi as any).suffix}
              subtitle={kpi.subtitle}
              loading={loading}
            />
          </Box>
        ))}
      </Box>

      {/* ── Funnel ──────────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', flexGrow: 1 }}>
            Funnel
          </Typography>
          {(funnel?.bounced ?? 0) > 0 && (
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#7D1935', fontWeight: 600 }}>
              {funnel?.bounced} bounced (excluded)
            </Typography>
          )}
        </Box>

        {loading ? (
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
        ) : stages.length === 0 || (funnel?.invited ?? 0) === 0 ? (
          <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#9CA3AF' }}>
              No invitations issued yet
            </Typography>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#9CA3AF' }}>
              Run <code>send-campaign.mjs</code> to invite the send list, then sync.
            </Typography>
          </Box>
        ) : (
          <FunnelChart stages={stages} bounced={funnel?.bounced ?? 0} />
        )}

        <DataSourceAttribution
          explainer={`Invitations and page views are first-party. Delivery, open and click come from Brevo — last synced ${relativeTime(funnel?.last_synced_at ?? null)}, and refreshed automatically every 15 minutes.`}
          source="DynamoDB (lifescribe-survey-*) + Brevo /smtp/statistics/events"
          endpoint="/api/admin/survey/funnel"
          computedAt={funnel?.last_synced_at || undefined}
        />
      </Paper>

      {/* ── Email copy ─────────────────────────────────────────────────── */}
      {/* Above the schedule, and separate from it: this card owns what the emails
          SAY, the one below owns WHEN the reminders go out. Keeping them apart
          avoids two competing save semantics in a single card. */}
      <SurveyEmailsCard refreshKey={refreshKey} campaign={campaign} />

      {/* ── Follow-up scheduling ───────────────────────────────────────── */}
      <FollowUpSettings refreshKey={refreshKey} campaign={campaign} />

      {/* ── Age × reason ─────────────────────────────────────────────── */}
      <SurveyAgeReasonChart campaign={campaign} />

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 2, borderColor: TAB_COLORS[tab] || TAB_COLORS[0], mb: 0 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          TabIndicatorProps={{ style: { backgroundColor: TAB_COLORS[tab] || TAB_COLORS[0], height: 3 } }}
        >
          {['Recipients', 'Responses', 'Question breakdown'].map((label, i) => (
            <Tab
              key={label}
              label={label}
              id={`survey-tracking-tab-${i}`}
              aria-controls={`survey-tracking-tabpanel-${i}`}
              sx={{
                minHeight: 56,
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                '&.Mui-selected': { color: TAB_COLORS[i] },
              }}
            />
          ))}
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <RecipientsTab refreshKey={refreshKey} campaign={campaign} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <ResponsesTab refreshKey={refreshKey} campaign={campaign} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <QuestionBreakdownTab refreshKey={refreshKey} campaign={campaign} />
      </TabPanel>
    </Box>
  );
};

export default SurveyTrackingPage;
